import { FC, ReactNode } from 'react'
import { format } from 'date-fns'
import { motion } from 'framer-motion'
import { CaptionLG, HeadingSM, TitleLG } from '@/atoms/Text'
import { SweepstakeObject } from '@/types/codegen-federation'
import { useTranslate } from '@/utils/translate/translate-client'
import { useGiveawaySchedule } from '../../hooks/useGiveawaySchedule'
import { CountdownToMidnight } from './CountdownToMidnight'

interface Props {
  theatricalSlug: string
  winnerAnnouncedDate: SweepstakeObject['winnerAnnouncedDate']
  isActive: boolean
}

export const GiveawayPromoBanner: FC<Props> = ({ theatricalSlug, winnerAnnouncedDate, isActive }) => {
  const { t } = useTranslate('tickets')
  const isDarkText = false
  const { refetch, isReady, dailyBonusEntries } = useGiveawaySchedule(theatricalSlug)

  if (!isReady) return null

  if (!isActive) {
    return (
      <BannerWrapper>
        <CaptionLG color={isDarkText ? 'black' : 'white'} className="md:photon-title-sm uppercase">
          {t('giveawayHasEnded', 'Giveaway Has Ended')}
        </CaptionLG>
        <TitleLG color={isDarkText ? 'black' : 'white'} weight="bold">
          {t('winnerAnnouncedGiveaway', 'Winner Announced {{winnerDate}}', {
            winnerDate: format(winnerAnnouncedDate, 'MMMM d, yyyy'),
          })}
          *
        </TitleLG>
      </BannerWrapper>
    )
  }

  return (
    <BannerWrapper>
      <CaptionLG color={isDarkText ? 'black' : 'white'} className="md:photon-title-sm uppercase">
        {t('todayOnly', 'Today Only')}
      </CaptionLG>

      <HeadingSM color={isDarkText ? 'black' : 'white'} className="md:photon-heading-lg" weight="bold">
        {t('getBonusEntriesGiveaway', 'Get {{dailyBonusEntries}} Bonus Entries', { dailyBonusEntries })}
      </HeadingSM>

      <CountdownToMidnight onCountdownEnd={refetch} isDarkText={isDarkText} />
    </BannerWrapper>
  )
}

const containerVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
}

const BannerWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="-mx-6 bg-cool-01 py-2.5 text-center md:mx-0 md:w-fit md:px-6 md:py-4 md:text-left"
    >
      {children}
    </motion.div>
  )
}
